import React from 'react';
import { useTranslation } from 'react-i18next';
import { useProgrammerContext } from '@/hid/ProgrammerContext';
import { ChipDB } from '@/target/ChipDB';
import type { ChipDefinition } from '@/types/hid8051';

const ChipSelector: React.FC = () => {
  const { t } = useTranslation('webflash');
  const { selectedChip, selectChip, connectTarget, targetInfo, status } = useProgrammerContext();

  const isConnected = status === 'connected';
  const isBusy = status === 'busy';

  const nuvotonChips = ChipDB.getByFamily('nuvoton-8051');
  const sonix8051Chips = ChipDB.getByFamily('sonix-8051');
  const sonixCm0Chips = ChipDB.getByFamily('sonix-cm0');

  const handleChipChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const chipName = e.target.value;
    if (!chipName) {
      selectChip(null);
      return;
    }
    const chip = ChipDB.getByName(chipName);
    selectChip(chip ?? null);
  };

  const renderGroup = (label: string, chips: ChipDefinition[]) => {
    if (chips.length === 0) return null;
    return (
      <optgroup label={label}>
        {chips.map((chip) => (
          <option key={chip.name} value={chip.name}>
            {chip.name} ({Math.round(chip.flashSize / 1024)} KB)
          </option>
        ))}
      </optgroup>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('chip.title')}
      </h3>

      <div className="space-y-4">
        {/* Chip dropdown */}
        <select
          value={selectedChip?.name ?? ''}
          onChange={handleChipChange}
          disabled={!isConnected}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm disabled:opacity-50"
        >
          <option value="">{t('chip.selectChip')}</option>
          {renderGroup(t('chip.nuvoton8051'), nuvotonChips)}
          {renderGroup(t('chip.sonix8051'), sonix8051Chips)}
          {renderGroup(t('chip.sonixCm0'), sonixCm0Chips)}
        </select>

        {/* Connect target button */}
        <button
          onClick={connectTarget}
          disabled={!isConnected || !selectedChip || isBusy}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {t('chip.connectTarget')}
        </button>

        {/* Target info */}
        {targetInfo && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">{t('chip.deviceId')}</span>
            <span className="font-mono text-gray-900 dark:text-white">
              0x{targetInfo.deviceId.toString(16).toUpperCase().padStart(8, '0')}
            </span>
            {targetInfo.uid && (
              <>
                <span className="text-gray-500 dark:text-gray-400">{t('chip.uid')}</span>
                <span className="font-mono text-gray-900 dark:text-white text-xs">
                  {Array.from(targetInfo.uid)
                    .map((b) => b.toString(16).toUpperCase().padStart(2, '0'))
                    .join(' ')}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChipSelector;
